import PropTypes from 'prop-types';

import { FormattedDate, FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import Box from '@zenbusiness/zui/Box';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import { getProductNameMessageId } from '../constants';
import {
  StyledCardRow,
  StyledCardDivider,
  StyledSummaryRow,
  StyledSummaryDivider
} from '../SubscriptionCancelPage.styles';

const SUMMARY_ROW_2_SX = {
  flexDirection: 'column',
  alignItems: 'flex-start'
};

function renderSubscriptionTitle({ titleMessageId, fallbackMessageId, displayName }) {
  if (titleMessageId) return <FormattedMessage id={titleMessageId} />;
  if (fallbackMessageId) return <FormattedMessage id={fallbackMessageId} />;
  return displayName;
}

// Shared subscription summary for cancel flow: used on confirm step (variant="card") and confirmation step (variant="summary").
// titleMessageId (product-catalog) preferred; fallback to summary or service.
export function SubscriptionSummaryContent({
  period,
  summary,
  service,
  titleMessageId,
  price,
  termStartDate,
  inTrial,
  trialDays,
  variant = 'card'
}) {
  const { formatNumber } = useIntl();

  const isSummary = variant === 'summary';
  const Row = isSummary ? StyledSummaryRow : StyledCardRow;
  const Divider = isSummary ? StyledSummaryDivider : StyledCardDivider;
  const row2Sx = isSummary ? SUMMARY_ROW_2_SX : undefined;

  const fallbackMessageId = getProductNameMessageId(service, summary);
  const displayName = summary || service;
  const displayPrice = formatNumber(price ?? 0, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });

  const titleContent = renderSubscriptionTitle({
    titleMessageId,
    fallbackMessageId,
    displayName
  });

  return (
    <>
      <Row>
        <Box flex={1}>
          <Text size="sm" weight="bold">
            {titleContent}
          </Text>
          <Text size="xs" color="secondary">
            <FormattedMessage id="dashboard.subscriptionCancel.subscription.period.label" values={{ period }} />
          </Text>
        </Box>
        <Box flexShrink={0} textAlign="right">
          <Text size="sm">
            <Text component="span" weight="bold">{displayPrice}</Text>
            <Text component="span">
              {' / '}
              <FormattedMessage id="dashboard.subscriptionCancel.subscription.interval.label" values={{ period }} />
            </Text>
          </Text>
        </Box>
      </Row>

      <Divider />

      <Row sx={row2Sx}>
        <Stack gap={0.5}>
          <Text size="xs" color="secondary">
            <FormattedMessage id="dashboard.subscriptionCancel.subscription.startDate" />
          </Text>
          <Text size="sm" weight="bold">
            {termStartDate
              ? <FormattedDate value={new Date(termStartDate)} month="2-digit" day="2-digit" year="numeric" />
              : null}
          </Text>
          {inTrial && trialDays > 0 && (
            <Text size="xs" color="secondary">
              <FormattedMessage
                id="dashboard.subscriptionCancel.trial.daysWarning"
                values={{ days: trialDays }}
              />
            </Text>
          )}
        </Stack>
      </Row>
    </>
  );
}

SubscriptionSummaryContent.propTypes = {
  period: PropTypes.string.isRequired,
  summary: PropTypes.string,
  service: PropTypes.string,
  titleMessageId: PropTypes.string,
  price: PropTypes.number.isRequired,
  termStartDate: PropTypes.string,
  inTrial: PropTypes.bool,
  trialDays: PropTypes.number,
  variant: PropTypes.oneOf(['card', 'summary'])
};
